/* eslint-disable jsx-a11y/anchor-is-valid */
import {Button} from "@progress/kendo-react-buttons"
import {State} from "@progress/kendo-data-query"
import {ExcelExport} from "@progress/kendo-react-excel-export"
import {
    Grid,
    GridCellProps,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridColumnMenuFilter,
    GridNoRecords,
    GridToolbar,
} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import {Tooltip} from "@progress/kendo-react-tooltip"
import {useEffect, useRef, useState} from "react"
import {RootState} from "../../../rootReducer"
import {parseXLStrValToNum, processExportData} from "../../../utils/common"
import {useAppDispatch, useAppSelector} from "../../../store"
import {SpinnerGrid} from "../../../shared/CommonItems"
import {FeesReport, setSelectedBookingId, toggleTestDialog} from "../../../containers/MotBooking/bookingSlice"
import {process} from "@progress/kendo-data-query"
import {columnProps} from "../../../shared/ActiveGridFilter"

const FeesReportGrid = () => {
    const dispatch = useAppDispatch()
    const intl = useInternationalization()
    const initialState: State = {skip: 0, take: 100}
    const feesReports = useAppSelector((state: RootState) => state.containers.motBooking.feeReport)
    const feesReportLoader = useAppSelector((state: RootState) => state.containers.motBooking.feeReportLoading)
    const _export = useRef<ExcelExport | null>(null)
    const [dataState, setDataState] = useState<State>(initialState)

    useEffect(() => {
        setDataState(initialState)
    }, [feesReports])

    const CustomColumnCell = (props: GridCellProps) => (
        <td title={props.dataItem[`${props.field}`]}>{props.dataItem[`${props.field}`]}</td>
    )

    const ReminderColumnCell = (props: GridCellProps) => (
        <td>
            <span className={props.dataItem[`${props.field}`] === "Pending" ? "text-danger" : ""}>
                {props.dataItem[`${props.field}`]}
            </span>
        </td>
    )

    const formatDateCell = (props: GridCellProps) => {
        const dateVal =
            props.dataItem[`${props.field}`] == null || props.dataItem[`${props.field}`] === ""
                ? ""
                : intl.formatDate(new Date(props.dataItem[`${props.field}`]))
        return <td>{dateVal}</td>
    }

    const CustomColumnCellSticky = (props: GridCellProps) => (
        <td
            style={props.style}
            className={props.className}
            colSpan={props.colSpan}
            role={"gridcell"}
            title={props.dataItem[`${props.field}`]}
        >
            {props.dataItem[`${props.field}`]}
        </td>
    )

    const openEditBookingDialog = (e: any, props: any) => {
        e.preventDefault()
        dispatch(setSelectedBookingId(props.dataItem.bookingID))
        props.dataItem.bookingTypeID === 1 && dispatch(toggleTestDialog(true))
    }

    const setBookingRegLinkColor = (dataItem: FeesReport) => {
        if (dataItem.bookingTypeID === 1 && dataItem.internal) {
            return "#447F83"
        }
        return "#D65549"
    }

    const AddLinkCell = (props: any) => (
        <td style={{left: "0px", color: setBookingRegLinkColor(props.dataItem)}} className="k-grid-content-sticky">
            <a
                style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                }}
                onClick={event => openEditBookingDialog(event, props)}
            >
                {props.dataItem.bookingRef}
            </a>
        </td>
    )

    const DurationCell = (props: GridCellProps) => {
        return (
            <td
                className={`${props.className} k-column-align-center`}
                style={props.style}
                colSpan={props.colSpan}
                role={"gridcell"}
            >
                {props.dataItem[`${props.field}`]}
            </td>
        )
    }

    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save()
        }
    }

    const formatExcelDateCell = (value: any) => {
        const dateVal = value == null || value === "" ? "" : intl.formatDate(new Date(value))
        return dateVal
    }

    const formatExcelCurrencyCell = (value: any) => {
        const currencyVal = value == null || value === "" ? "" : intl.formatNumber(value, "c")
        return currencyVal
    }

    const exportExcelData = (feesReports: FeesReport[]) =>
        feesReports!.map(({date, testFee, laneFee, account, total, ...item}) => ({
            date: formatExcelDateCell(date),
            testFee: formatExcelCurrencyCell(testFee),
            laneFee: formatExcelCurrencyCell(laneFee),
            total: formatExcelCurrencyCell(total),
            account: parseXLStrValToNum(account),
            ...item,
        }))

    const ColumnMenuCheckboxFilter = (props: any, data: FeesReport[] | null) => (
        <GridColumnMenuCheckboxFilter {...props} data={data} expanded={true} />
    )

    return (
        <div className="position-relative" id="grid-fee-report">
            {feesReportLoader && <SpinnerGrid />}
            <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                <ExcelExport
                    fileName={crypto.randomUUID()}
                    data={exportExcelData(processExportData(feesReports, dataState))}
                    ref={_export}
                >
                    <Grid
                        style={{
                            height: "calc(100vh - 14.3rem)",
                        }}
                        data={process(feesReports, dataState)}
                        {...dataState}
                        pageable
                        sortable
                        onDataStateChange={event => setDataState(event.dataState)}
                    >
                        <GridNoRecords>&nbsp;</GridNoRecords>
                        <GridToolbar>
                            <Button
                                fillMode={"flat"}
                                themeColor="primary"
                                className="buttons-container-button"
                                iconClass="fa fa-file-excel"
                                onClick={excelExport}
                                disabled={process(feesReports, dataState).total === 0}
                            >
                                Export to Excel
                            </Button>
                        </GridToolbar>
                        <GridColumn
                            {...columnProps("bookingRef", dataState)}
                            title="Booking Ref"
                            field="bookingRef"
                            width={150}
                            cell={props => <AddLinkCell {...props} />}
                            locked
                        />
                        <GridColumn
                            {...columnProps("date", dataState)}
                            title="Test Date"
                            field="date"
                            filter={"date"}
                            format="{0:d}"
                            width={"140"}
                            locked
                            columnMenu={props => {
                                let prop = {...props}
                                prop.column.field = "date"
                                return (
                                    <GridColumnMenuFilter
                                        {...prop}
                                        hideSecondFilter={true}
                                        expanded={true}
                                        filterOperators={{
                                            date: [
                                                {text: "grid.filterEqOperator", operator: "eq"},
                                                {text: "grid.filterNotEqOperator", operator: "neq"},
                                                {text: "grid.filterAfterOrEqualOperator", operator: "gte"},
                                                {text: "grid.filterAfterOperator", operator: "gt"},
                                                {text: "grid.filterBeforeOperator", operator: "lt"},
                                                {text: "grid.filterBeforeOrEqualOperator", operator: "lte"},
                                            ],
                                        }}
                                    />
                                )
                            }}
                        />
                        <GridColumn title="Time" field="timing" filterable={false} width={"140"} locked />
                        <GridColumn title="Duration" field="testDuration" width="140px" cell={DurationCell} locked />
                        <GridColumn
                            {...columnProps("customerName", dataState)}
                            title="Customer Name"
                            field="customerName"
                            width={250}
                            cell={CustomColumnCellSticky}
                            locked
                        />
                        <GridColumn
                            {...columnProps("registration", dataState)}
                            title="Vehicle Reg"
                            field="registration"
                            cell={CustomColumnCellSticky}
                            width="140px"
                            locked
                        />
                        <GridColumn
                            {...columnProps("dealer", dataState)}
                            title="Dealer"
                            field="dealer"
                            width={"120px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("preparerID", dataState)}
                            title="PreparerID"
                            field="preparerID"
                            width={"140px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("teststationName", dataState)}
                            title="Test Station"
                            field="teststationName"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("jobNumber", dataState)}
                            title="Job No."
                            field="jobNumber"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("account", dataState)}
                            title="Account"
                            field="account"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("customerOrderRef", dataState)}
                            title="Customer Ref"
                            field="customerOrderRef"
                            width="200px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("testPackage", dataState)}
                            title="Test Package"
                            field="testPackage"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("testType", dataState)}
                            title="Test Type"
                            field="testType"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("tranType", dataState)}
                            title="Transaction Type"
                            field="tranType"
                            width={"180px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("recipients", dataState)}
                            title="Recipients"
                            field="recipients"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("email", dataState)}
                            title="Email"
                            field="email"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("phone", dataState)}
                            title="Phone"
                            field="phone"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("notes", dataState)}
                            title="Notes"
                            field="notes"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("bookingStatus", dataState)}
                            columnMenu={props => ColumnMenuCheckboxFilter(props, feesReports)}
                            title="Status"
                            field="bookingStatus"
                            width="160px"
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("reminderCall", dataState)}
                            columnMenu={props => ColumnMenuCheckboxFilter(props, feesReports)}
                            title="Reminder Call"
                            width={"140px"}
                            field="reminderCall"
                            cell={ReminderColumnCell}
                        />
                        <GridColumn
                            {...columnProps("sendreminderEmail", dataState)}
                            columnMenu={props => ColumnMenuCheckboxFilter(props, feesReports)}
                            title="Reminder Email"
                            width={"140px"}
                            field="sendreminderEmail"
                            cell={ReminderColumnCell}
                        />
                        <GridColumn
                            {...columnProps("testFee", dataState)}
                            title="Test Fee"
                            field="testFee"
                            width={"140px"}
                            cell={(props: any) => (
                                <td className="text-end pe-5" title={props.dataItem[`${props.field}`]}>
                                    {intl.formatNumber(props.dataItem.testFee, {
                                        style: "currency",
                                        minimumFractionDigits: 2,
                                    })}
                                </td>
                            )}
                        />
                        <GridColumn
                            {...columnProps("laneFee", dataState)}
                            title="Lane Fee"
                            field="laneFee"
                            width={"140px"}
                            cell={(props: any) => (
                                <td className="text-end pe-5" title={props.dataItem[`${props.field}`]}>
                                    {intl.formatNumber(props.dataItem.laneFee, {
                                        style: "currency",
                                        minimumFractionDigits: 2,
                                    })}
                                </td>
                            )}
                        />
                        <GridColumn
                            {...columnProps("total", dataState)}
                            title="Total"
                            field="total"
                            width={"140px"}
                            cell={(props: any) => (
                                <td className="text-end pe-5" title={props.dataItem[`${props.field}`]}>
                                    {intl.formatNumber(props.dataItem.total, {
                                        style: "currency",
                                        minimumFractionDigits: 2,
                                    })}
                                </td>
                            )}
                        />
                        <GridColumn
                            {...columnProps("make", dataState)}
                            title="Make"
                            field="make"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("model", dataState)}
                            title="Model"
                            field="model"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("vehicleClass", dataState)}
                            title="Vehicle Class"
                            field="vehicleClass"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("manufactureDate", dataState)}
                            title="Manufacture Date"
                            field="manufactureDate"
                            width={"160px"}
                            cell={formatDateCell}
                            filter={"date"}
                            format="{0:d}"
                        />
                        <GridColumn
                            {...columnProps("registrationDate", dataState)}
                            title="Registration Date"
                            field="registrationDate"
                            width={"160px"}
                            cell={formatDateCell}
                            filter={"date"}
                            format="{0:d}"
                        />
                        <GridColumn
                            {...columnProps("vehicleType", dataState)}
                            title="Vehicle Type"
                            field="vehicleType"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("axles", dataState)}
                            title="Axle"
                            field="axles"
                            width={"160px"}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("failureReason", dataState)}
                            title="Failure Reason"
                            field="failureReason"
                            width={150}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("failureDescription", dataState)}
                            title="Failure Description"
                            field="failureDescription"
                            width={150}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("preparedByDealer", dataState)}
                            columnMenu={props => ColumnMenuCheckboxFilter(props, feesReports)}
                            title="Affects Pass Rate"
                            field="preparedByDealer"
                            cell={CustomColumnCell}
                            width={200}
                        />
                        <GridColumn
                            {...columnProps("annualTestDate", dataState)}
                            field="annualTestDate"
                            cell={formatDateCell}
                            title="Annual Test date"
                            filter={"date"}
                            format="{0:d}"
                            width={200}
                        />
                        <GridColumn
                            {...columnProps("annualTestType", dataState)}
                            field="annualTestType"
                            title="Test type"
                            cell={CustomColumnCell}
                            width={150}
                        />
                        <GridColumn
                            {...columnProps("testResult", dataState)}
                            field="testResult"
                            title="Test Result"
                            width={150}
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("testCertificateNumber", dataState)}
                            field="testCertificateNumber"
                            width={150}
                            title="Certificate No."
                            cell={CustomColumnCell}
                        />
                        <GridColumn
                            {...columnProps("expiryDate", dataState)}
                            field="expiryDate"
                            cell={formatDateCell}
                            title="AnnualTest Expiry Date"
                            filter={"date"}
                            width={210}
                            format="{0:d}"
                        />
                        <GridColumn
                            {...columnProps("numberOfDefectsAtTest", dataState)}
                            field="numberOfDefectsAtTest"
                            title="Defects"
                            width={150}
                        />
                        <GridColumn
                            {...columnProps("numberOfAdvisoryDefectsAtTest", dataState)}
                            field="numberOfAdvisoryDefectsAtTest"
                            title="Advisory defects"
                            width={200}
                        />
                        <GridColumn
                            {...columnProps("location", dataState)}
                            field="location"
                            title="Location"
                            cell={CustomColumnCell}
                            width={400}
                        />
                        <GridColumn
                            {...columnProps("validResults", dataState)}
                            field="validResults"
                            title="Valid Results"
                            cell={CustomColumnCell}
                            width={135}
                        />
                        <GridColumn
                            {...columnProps("validResultReason", dataState)}
                            field="validResultReason"
                            title="Valid Result Reason"
                            cell={CustomColumnCell}
                            width={300}
                        />
                        <GridColumn
                            {...columnProps("updatedDate", dataState)}
                            title="Updated Date"
                            field="updatedDate"
                            width={"160px"}
                            filter={"date"}
                            format="{0:d}"
                        />
                        <GridColumn
                            {...columnProps("updatedBy", dataState)}
                            title="Updated By"
                            field="updatedBy"
                            width={"135px"}
                            cell={CustomColumnCell}
                        />
                    </Grid>
                </ExcelExport>
            </Tooltip>
        </div>
    )
}

export default FeesReportGrid
